import { createSlice } from "@reduxjs/toolkit";
import { AppState } from "./AppState";
import { AlertColor } from "@mui/material";
import { MenuConfigModule } from "../data/menu/MenuModule";

export interface ErrorSnackBarState {
  message: string;
  type: AlertColor | undefined;
}

export interface UserSlice {
  userToken: string | null;
  userData: any | null;
  userMenuFiltered: any;
  isLoading: boolean;
  displayErrorSnackBar: ErrorSnackBarState | null;
  viewsToDisplay: { home: boolean };
  allUsers: any;
  allWebsites: any;
  currentPath: any;
}

const initialState: UserSlice = {
  userToken: null,
  userData: null,
  userMenuFiltered: null,
  isLoading: false,
  displayErrorSnackBar: null,
  viewsToDisplay: { home: true },
  allUsers: [],
  allWebsites: [],
  currentPath: "",
} as const;

export const userSlice = createSlice({
  name: "user",
  initialState,

  reducers: {
    setSignedOut: (state) => {
      state.userToken = null;
    },
    setUserData: (state, action) => {
      state.userData = action.payload;
    },
    setUserMenuFiltered: (state, action) => {
      state.userMenuFiltered = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setDisplayErrorSnackBar: (state, action) => {
      state.displayErrorSnackBar = action.payload;
    },
    setViewsToDisplay: (state, action) => {
      state.viewsToDisplay = action.payload;
    },
    setUserToken: (state, action) => {
      state.userToken = action.payload;
    },
    setAllUsers: (state, action) => {
      state.allUsers = action.payload;
    },
    setAllWebsites: (state, action) => {
      state.allWebsites = action.payload;
    },
    setCurrentPath: (state, action) => {
      state.currentPath = action.payload;
    },
  },
});

export const {
  setSignedOut,
  setUserData,
  setIsLoading,
  setViewsToDisplay,
  setDisplayErrorSnackBar,
  setUserToken,
  setAllUsers,
  setAllWebsites,
  setCurrentPath,
  setUserMenuFiltered,
} = userSlice.actions;

// We export this that we could get the user state (loggedIn/loggedOut)
// export const selectUser = (state: { user: { user: any; }; }) => state.user.user;

export const selectSignedIn = (state: AppState): boolean =>
  !!state.user.userToken;
export const selectIsLoading = (state: AppState) => state.user.isLoading;
export const selectUserData = (state: AppState): any | null =>
  state.user.userData ?? null;
export const selectUserMenu = (state: AppState) =>
  state.user.userData?.data?.products ?? null;
export const selectUserDetails = (state: AppState) =>
  state.user.userData?.data?.user_details ?? null;
export const selectUserWebsitePermissions = (state: AppState) =>
  state.user.userData?.data?.website_permissions ?? null;

export const selectViewsToDisplay = (state: AppState) =>
  state.user.viewsToDisplay;
export const selectDisplayErrorSnackBar = (state: AppState) =>
  state.user.displayErrorSnackBar;
export const selectUserToken = (state: AppState): string | null =>
  state.user.userToken;

export const selectAllUsers = (state: AppState) => state.user.allUsers;

export const selectAllWebsites = (state: AppState) => state.user.allWebsites;

export const selectCurrentPath = (state: AppState) => state.user.currentPath;

export const selectUserMenuFiltered = (
  state: AppState
): MenuConfigModule[] | null => state.user.userMenuFiltered ?? null;

export default userSlice.reducer;
